/**
 * TokenQuotaWarningBanner - Site-wide banner for token quota warnings
 *
 * Displays when user approaches monthly token limit (>= 80%).
 * Dismissible but reappears on page reload.
 */

import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface TokenQuotaWarningBannerProps {
  used: number;
  total: number;
  percentage: number;
  planName: string;
  upgradeUrl?: string;
  onDismiss: () => void;
}

export const TokenQuotaWarningBanner: React.FC<TokenQuotaWarningBannerProps> = ({
  used,
  total,
  percentage,
  planName,
  upgradeUrl = '/signup',
  onDismiss
}) => {
  // Format token numbers with commas
  const formatTokens = (num: number) => num.toLocaleString();

  return (
    <div className="bg-amber-500/20 border-b border-amber-500/50 px-4 py-3 relative z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <span className="text-amber-400 text-xl">⚠️</span>
          <div className="text-sm">
            <span className="text-amber-200 font-medium">Token limit warning</span>
            <span className="text-amber-300 ml-2">
              ({formatTokens(used)}/{formatTokens(total)} tokens used · {percentage.toFixed(0)}% · {planName})
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={upgradeUrl}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
          >
            Upgrade Plan
          </Link>
          <button
            onClick={onDismiss}
            className="text-amber-300 hover:text-amber-100 transition-colors p-1"
            aria-label="Dismiss banner"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
